// src/components/ReportCommessaTab.js
import React, { useState, useEffect, useCallback } from "react";
import { statoNf, haOdaValido } from "../utils/acquisti";
import { collection, query, where, getDocs, addDoc, updateDoc, deleteDoc,
  doc, serverTimestamp, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { prossimoProgressivo } from "../utils/contatoriUtils";
import { datiAzienda, esc } from "../utils/cartaIntestata";
import { useAuth } from "../contexts/AuthContext";
import { useFeatures } from "../contexts/FeaturesContext";
import { useToast } from "../utils/Toast";
import { confirm } from "../utils/confirm";
import { FileText, Plus, Printer, X, ChevronDown, ChevronUp,
  Lock, AlertTriangle, CheckCircle, Clock, TrendingUp } from "lucide-react";
import { FONTI_ORE, costoManodoperaVoci } from "../utils/oreCommessa";
import { caricaVociOre, costiCalcolatiDalVivo } from "../utils/costiDalVivo";
import { toLocalDateStr } from "../utils/dateUtils";
import { STATI_ODA_CONTEGGIATI, imponibileOda } from "../utils/cabinaDiRegia";
import { contaComeCosto, eRicavo, budgetPrincipale, dataMovimento, totaliCommessa, importoCommessa, numIt, variantiDaBudget } from "../utils/economiaCommessa";
import { riepilogoVarianti } from "../utils/varianti";
import { isTaskOverdue } from "../utils/taskHelpers";
import { dominioSito } from "../utils/fornitore";

// ─── Costanti ────────────────────────────────────────────────────────────────
const TIPI_REPORT = {
  periodico: { label: "Report Periodico",  codice: "RPC", emoji: "📊" },
  chiusura:  { label: "Report di Chiusura", codice: "RCC", emoji: "📋" },
};

const STATI_REPORT = {
  bozza:       { label: "Bozza",       bg: "#f3f4f6", col: "#6b7280" },
  consolidato: { label: "Consolidato", bg: "#d1fae5", col: "#065f46" },
  annullato:   { label: "Annullato",   bg: "#fee2e2", col: "#991b1b" },
};

const SEZIONI_DISPONIBILI = [
  { id: "riepilogo",   label: "Riepilogo generale",       emoji: "📌", riservata: false },
  { id: "attivita",    label: "Attività del periodo",      emoji: "✅", riservata: false },
  { id: "cronoprogramma", label: "Cronoprogramma",         emoji: "📅", riservata: false },
  { id: "personale",   label: "Personale e ore",           emoji: "👷", riservata: false },
  { id: "giornale",    label: "Giornale di cantiere",      emoji: "📒", riservata: false },
  { id: "sopralluoghi",label: "Sopralluoghi e interventi", emoji: "🔍", riservata: false },
  { id: "ddt",         label: "DDT",                       emoji: "📄", riservata: false },
  { id: "materiali",   label: "Materiali (NF e richieste)", emoji: "🧰", riservata: false },
  { id: "rientroMateriali", label: "Rientro Materiali",    emoji: "🔄", riservata: false },
  { id: "verbali",     label: "Verbali di riunione",       emoji: "📝", riservata: false },
  { id: "eventi",      label: "Eventi significativi",      emoji: "📆", riservata: false },
  { id: "criticita",   label: "Criticità e anomalie",      emoji: "⚠️",  riservata: false },
  { id: "prossime",    label: "Prossime attività",         emoji: "🎯", riservata: false },
  { id: "segnalazioni",label: "Segnalazioni Cliente",      emoji: "📣", riservata: false },
  { id: "commerciale", label: "Inoltro costi per offerta al cliente",     emoji: "💼", riservata: true  },
  { id: "notespese",   label: "Note spese",                emoji: "🧾", riservata: true  },
  { id: "economico",   label: "Sezione economica",         emoji: "💰", riservata: true  },
];

const SEZIONI_DEFAULT_PERIODICO = ["riepilogo","attivita","cronoprogramma","personale","giornale","sopralluoghi","ddt","materiali","rientroMateriali","eventi","criticita","prossime","segnalazioni","commerciale"];
const SEZIONI_DEFAULT_CHIUSURA  = ["riepilogo","attivita","cronoprogramma","personale","giornale","sopralluoghi","ddt","materiali","rientroMateriali","verbali","eventi","criticita","segnalazioni","commerciale","notespese","economico"];

// ─── Utility ─────────────────────────────────────────────────────────────────
const fmtData = d => d ? new Date(d + "T00:00:00").toLocaleDateString("it-IT") : "—";
const fmtDataOra = d => d?.toDate ? d.toDate().toLocaleString("it-IT") : (d ? new Date(d).toLocaleString("it-IT") : "—");
const fmtEuro = n => (parseFloat(n)||0).toLocaleString("it-IT",{minimumFractionDigits:2});
// W451: le attività si chiamano «title» e il responsabile è «assignedName» (prima il report
// cercava «name» e «assigneeName», che non esistono, e mostrava solo trattini)
const nomeAttivita = (t) => t?.title || t?.name || "—";
const responsabileAttivita = (t) => t?.assignedName || t?.assigneeName || t?.assigneeNome || "—";
const STATI_CHIUSI = ["completed", "completata", "annullata"];
// Gli eventi del calendario della commessa hanno il giorno in «dueDate»
const dataEvento = (e) => String(e?.dueDate || e?.data || e?.dataInizio || "");
const chiusa = (t) => STATI_CHIUSI.includes(t?.status);
/** Giorno in cui l'attività è stata completata (mai la scadenza al suo posto). */
function dataCompletamento(t) {
  const d = t?.completedAt?.toDate ? t.completedAt.toDate() : (t?.completedAt?.seconds ? new Date(t.completedAt.seconds * 1000) : null);
  return d ? toLocalDateStr(d) : null;
}
// W451: con un periodo scelto, un dato senza data NON rientra nel periodo (prima una data
// mancante valeva «sì» e lo stesso dato finiva in ogni report periodico)
// W453: manodopera, «Le mie ore» e noleggi del report completo, una riga ciascuno (stesse regole di costiCalcolatiDaBudget)
function righeCalcolate(cc, ora) {
  if (!cc) return [];
  const quando = ora ? "calcolati alla generazione del report" : "all'ultima apertura del Controllo Economico";
  const n = (v) => numIt(v);
  return [
    cc.fonteOre && cc.fonteOre !== "presenze" && n(cc.manodopera) ? { data: "", quando: "totale", categoria: "Manodopera", descrizione: `Ore dalla fonte dell'azienda, ${quando}`, importo: n(cc.manodopera) } : null,
    n(cc.diario) ? { data: "", quando: "totale", categoria: "Manodopera — Le mie ore", descrizione: `Ore del diario, ${quando}`, importo: n(cc.diario) } : null,
    n(cc.noleggi) ? { data: "", quando: "totale", categoria: "Noleggi mezzi d'opera", descrizione: `Tariffa e trasporti, ${quando}`, importo: n(cc.noleggi) } : null,
  ].filter(Boolean);
}
const isInPeriodo = (data, dal, al) => {
  if (!dal || !al) return true;
  if (!data) return false;
  const d = String(data).slice(0, 10);
  return d >= dal && d <= al;
};

// ─── Raccolta dati dal Firestore ──────────────────────────────────────────────
async function raccogliDati(projectId, orgId, dal, al, sezioni, versioneCliente) {
  const dati = {};
  const promises = [];

  if (sezioni.includes("attivita") || sezioni.includes("riepilogo") || sezioni.includes("prossime") || sezioni.includes("criticita")) {
    promises.push(
      getDocs(query(collection(db,"tasks"), where("projectId","==",projectId), where("organizationId","==",orgId)))
        .then(snap => { dati.tasks = snap.docs.map(d=>({id:d.id,...d.data()})); })
    );
  }
  if (sezioni.includes("cronoprogramma")) {
    promises.push(
      getDocs(query(collection(db,"cronoprogrammaFasi"), where("projectId","==",projectId), where("orgId","==",orgId)))
        .then(snap => { dati.fasi = snap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(a.ordine||0)-(b.ordine||0) || String(a.inizio||"").localeCompare(String(b.inizio||""))); })
    );
  }
  // W452: le ore servono anche alla sezione economica (costo della manodopera del periodo)
  if (sezioni.includes("personale") || (sezioni.includes("economico") && !versioneCliente)) {
    // Ore dalla STESSA fonte del Controllo Economico e della Cabina di Regia (utils/costiDalVivo.js):
    // Programmazione settimanale, Rapportini o Presenze, come impostato dall'azienda, più «Le mie ore».
    // W452: servono anche alla sezione economica (manodopera del periodo); W453: nel report completo
    // manodopera e noleggi si calcolano adesso, non si leggono più dall'ultima apertura del Controllo Economico.
    promises.push(
      (async () => {
        const orgSnap = await getDoc(doc(db, "organizations", orgId));
        const org = orgSnap.exists() ? orgSnap.data() : {};
        const ore = await caricaVociOre({ orgId, projectId, org });
        const orePerDipendente = {};
        const vociPeriodo = ore.voci.filter(v => isInPeriodo(v.data, dal, al));
        vociPeriodo.forEach(v => { orePerDipendente[v.nome] = (orePerDipendente[v.nome]||0) + v.ore; });
        if (sezioni.includes("personale")) dati.orePerDipendente = orePerDipendente;
        dati.fonteOre = FONTI_ORE[ore.fonte];
        dati.fonteId = ore.fonte;
        dati.manodoperaPeriodo = costoManodoperaVoci(vociPeriodo, ore.dipendenti);
        if (!(dal && al) && sezioni.includes("economico") && !versioneCliente) dati.costiCalcolatiOraPending = { org, ore }; // W453: si completa dopo, quando il budget è letto
      })().catch(err => { console.warn("Ore della commessa non lette:", err?.code || err); dati.orePerDipendente = {}; })
    );
  }
  if (sezioni.includes("giornale")) {
    promises.push(
      getDocs(query(collection(db,"giornaleCantiereDays"), where("projectId","==",projectId), where("orgId","==",orgId)))
        .then(snap => { dati.giornale = snap.docs.map(d=>({id:d.id,...d.data()})).filter(g=>isInPeriodo(g.data,dal,al)).sort((a,b)=>a.data?.localeCompare(b.data||"")||0); })
    );
  }
  if (sezioni.includes("sopralluoghi")) {
    promises.push(
      getDocs(query(collection(db,"sopralluoghi"), where("projectId","==",projectId), where("orgId","==",orgId)))
        .then(snap => { dati.sopralluoghi = snap.docs.map(d=>({id:d.id,...d.data()})).filter(s=>isInPeriodo(s.data,dal,al)); }),
      getDocs(query(collection(db,"rapportini"), where("projectId","==",projectId), where("orgId","==",orgId)))
        .then(snap => { dati.rapportini = snap.docs.map(d=>({id:d.id,...d.data()})).filter(r=>isInPeriodo(r.data,dal,al)); })
    );
  }
  if (sezioni.includes("ddt")) {
    promises.push(
      getDocs(query(collection(db,"ddt"), where("projectId","==",projectId), where("orgId","==",orgId)))
        .then(snap => { dati.ddt = snap.docs.map(d=>({id:d.id,...d.data()})).filter(d=>isInPeriodo(d.dataDocumento,dal,al)); })
    );
  }
  if (sezioni.includes("verbali")) {
    promises.push(
      getDocs(query(collection(db,"verbali"), where("projectId","==",projectId), where("orgId","==",orgId)))
        .then(snap => { dati.verbali = snap.docs.map(d=>({id:d.id,...d.data()})).filter(v=>isInPeriodo(v.data,dal,al)); })
    );
  }
  if (sezioni.includes("eventi")) {
    promises.push(
      getDocs(query(collection(db,"eventiCommessa"), where("projectId","==",projectId), where("organizationId","==",orgId)))
        .then(snap => { dati.eventi = snap.docs.map(d=>({id:d.id,...d.data()})).filter(e=>isInPeriodo(dataEvento(e),dal,al)); })
    );
  }
  if (sezioni.includes("materiali")) {
    promises.push(
      getDocs(query(collection(db,"nuoveForniture"), where("commessaId","==",projectId), where("organizationId","==",orgId)))
        .then(snap => { dati.nf = snap.docs.map(d=>({id:d.id,...d.data()})).filter(n=>!n.eliminata && (n.statoRichiesta||"Da inviare")!=="Da inviare" && isInPeriodo(n.dataRichiesta,dal,al)); }),
      getDocs(query(collection(db,"richiesteMateriali"), where("projectId","==",projectId), where("organizationId","==",orgId)))
        .then(snap => { dati.richiesteMat = snap.docs.map(d=>({id:d.id,...d.data()})).filter(r=>isInPeriodo(toLocalDateStr(r.createdAt?.toDate?.()),dal,al)); })
    );
  }
  if (sezioni.includes("rientroMateriali")) {
    promises.push(
      getDocs(query(collection(db,"rientriMateriali"), where("projectId","==",projectId), where("organizationId","==",orgId)))
        .then(snap => { dati.rientri = snap.docs.map(d=>({id:d.id,...d.data()})).filter(r=>isInPeriodo(r.dataRientro,dal,al)); })
    );
  }
  if (sezioni.includes("segnalazioni")) {
    promises.push(
      getDocs(query(collection(db,"segnalazioniCliente"), where("projectId","==",projectId), where("orgId","==",orgId)))
        .then(snap => { dati.segnalazioni = snap.docs.map(d=>({id:d.id,...d.data()})).filter(s=>isInPeriodo(s.dataRicezione,dal,al)); })
    );
  }
  if (sezioni.includes("notespese") && !versioneCliente) {
    promises.push(
      (async () => {
        const snap = await getDocs(query(collection(db,"noteSpese"), where("orgId","==",orgId)));
        const dipSnap = await getDocs(query(collection(db,"dipendenti"), where("organizationId","==",orgId)));
        const nomiMap = {};
        dipSnap.docs.forEach(d=>{ const dd=d.data(); if(dd.nexoraUid) nomiMap[dd.nexoraUid] = `${dd.nome||""} ${dd.cognome||""}`.trim(); });
        const righeCommessa = [];
        snap.docs.forEach(d => {
          const ns = d.data();
          Object.entries(ns.righe||{}).forEach(([giorno, r]) => {
            if (r.commessaId !== projectId) return;
            const dataStr = `${ns.anno}-${String(ns.mese).padStart(2,"0")}-${String(giorno).padStart(2,"0")}`;
            if (!isInPeriodo(dataStr, dal, al)) return;
            righeCommessa.push({ ...r, data: dataStr, dipendenteNome: nomiMap[ns.uid]||ns.uid||"—" });
          });
        });
        dati.noteSpese = righeCommessa;
      })()
    );
  }
  if (sezioni.includes("commerciale") && !versioneCliente) {
    promises.push(
      getDocs(query(collection(db,"richiesteCommerciali"), where("projectId","==",projectId), where("orgId","==",orgId)))
        .then(snap => { dati.richiesteComm = snap.docs.map(d=>({id:d.id,...d.data()})).filter(r=>(dal&&al?isInPeriodo(r.dataEmissione,dal,al):true) && r.stato!=="annullata"); })
    );
  }
  if (sezioni.includes("economico") && !versioneCliente) {
    promises.push(
      getDocs(query(collection(db,"movimentiEconomici"), where("projectId","==",projectId), where("organizationId","==",orgId)))
        // Data di competenza ricavata da tutti i formati dei movimenti (prima molti non avevano
        // "data" e finivano in ogni periodo, gonfiando i costi dei report periodici)
        .then(snap => { dati.movimenti = snap.docs.map(d=>({id:d.id,...d.data()})).filter(m=>isInPeriodo(dataMovimento(m),dal,al)); }),
      // Il budget si cerca per commessa: il documento NON ha come id la commessa (prima
      // getDoc(budgetCommessa/{projectId}) non lo trovava mai e il report mostrava ricavi zero)
      getDocs(query(collection(db,"budgetCommessa"), where("projectId","==",projectId), where("organizationId","==",orgId)))
        .then(snap => { if(!snap.empty) dati.budget = budgetPrincipale(snap.docs.map(d=>({id:d.id,...d.data()}))); }), // W452: come il Controllo Economico se per errore sono due
      // W451: varianti e lavori extra lette dal vivo, come il Controllo Economico — il valore della
      // commessa è contratto + varianti accettate (prima il report mostrava solo il contratto)
      getDocs(query(collection(db,"attivitaExtra"), where("projectId","==",projectId), where("orgId","==",orgId)))
        .then(snap => { dati.varianti = snap.docs.map(d=>({id:d.id,...d.data()})); })
        .catch(() => { dati.varianti = null; }),
      // Costi degli Ordini di Acquisto — vivono fuori da movimentiEconomici (calcolati dalle
      // righe dell'ODA, non scritti come movimento singolo): senza questa aggiunta il margine
      // del report risultava più alto di quello vero, perché ignorava del tutto i materiali
      // comprati tramite il ciclo NF → ODA → Fornitori. Stessa soglia di stati già usata nel
      // Controllo Economico — solo gli ordini avviati per davvero rappresentano un costo reale.
      getDocs(query(collection(db,"ordiniAcquisto"), where("commessaId","==",projectId), where("organizationId","==",orgId)))
        .then(snap => {
          // W452: l'elenco (non solo la somma), così la tabella del report mostra anche gli ordini
          dati.odaReport = snap.docs.map(d=>({id:d.id,...d.data()}))
            .filter(o=>!o.eliminato && STATI_ODA_CONTEGGIATI.includes(o.stato) && isInPeriodo(o.data,dal,al))
            .map(o=>({ data: o.data || "", numero: o.numeroODA || o.numeroOda || "ODA", fornitore: o.fornitoreNome || "", importo: imponibileOda(o) })); // IVA esclusa, come in Controllo Economico
        })
    );
  }

  await Promise.all(promises);
  // W453: manodopera e noleggi del report completo calcolati adesso (fallback: quelli salvati sul budget)
  if (dati.costiCalcolatiOraPending) {
    const { org, ore } = dati.costiCalcolatiOraPending;
    delete dati.costiCalcolatiOraPending;
    dati.costiCalcolatiOra = await costiCalcolatiDalVivo({ orgId, projectId, org, vociOre: ore, salvati: dati.budget?.costiCalcolati || null });
  }
  return dati;
}

// ─── Generatore PDF ───────────────────────────────────────────────────────────
export function generaPDF(config, dati, projectData, orgInfo, currentUser) {
  const { tipo, dal, al, versione, sezioni, noteIntro, noteFinale, numero } = config;
  const isCliente = versione === "cliente";
  const isChiusura = tipo === "chiusura";

  // Nome file: RP_Cliente_CodiceCommessa_NomeCommessa
  const tipoPrefix = isChiusura ? "RC" : "RP";
  const clienteSlug = (projectData?.clienteNome||"").replace(/\s+/g,"_").replace(/[^a-zA-Z0-9_]/g,"");
  const codiceSlug = (projectData?.codiceCommessa||"").replace(/[^a-zA-Z0-9-]/g,"");
  const nomeSlug = (projectData?.name||"").replace(/\s+/g,"_").replace(/[^a-zA-Z0-9_]/g,"");
  const nomeFile = [tipoPrefix, clienteSlug, codiceSlug, nomeSlug].filter(Boolean).join("_");

  const win = window.open("","_blank");

  // Carta intestata: stessi dati e scelte di Impostazioni Aziendali (utils/cartaIntestata.js)
  const { colore: coloreDoc, posizioneLogo } = datiAzienda(orgInfo);
  const escR = esc;
  const logoHtml = orgInfo?.logo ? `<img src="${escR(orgInfo.logo)}" style="max-height:54px;max-width:220px;object-fit:contain;"/>` : "";

  // Calcoli riepilogo
  const tasks = dati.tasks || [];
  const completate = tasks.filter(t=>t.status==="completed"||t.status==="completata");
  const inCorso = tasks.filter(t=>!chiusa(t)&&t.status!=="todo");
  const scadute = tasks.filter(isTaskOverdue); // W452: regola unica in tutta l'app (utils/taskHelpers.js)
  // W451: nel periodo = scadenza o completamento dentro il periodo (prima un'attività senza
  // scadenza finiva sempre nel periodo, perché una data mancante valeva «sì»)
  const nelPeriodo = (d) => !!d && d >= dal && d <= al;
  const taskPeriodo = dal && al ? tasks.filter(t=>t.status!=="annullata" && (nelPeriodo(t.dueDate)||nelPeriodo(dataCompletamento(t)))) : tasks.filter(t=>t.status!=="annullata");
  const progresso = tasks.length > 0 ? Math.round(completate.length/tasks.length*100) : 0;

  // Personale/ore — calcolate correttamente da raccogliDati (giorni[].timbrature[].commessaId)
  const personaleMap = dati.orePerDipendente || {};
  const oreTotal = Object.values(personaleMap).reduce((s,h)=>s+h,0);

  // Economico
  const movimenti = dati.movimenti || [];
  const budget = dati.budget || {};
  // "Ricavi" = valore della commessa (importoCommessa) + ricavi registrati come movimenti
  // (es. interventi di manutenzione a tariffa). Costi = movimenti di costo + ordini IVA esclusa.
  // Stesse regole del Controllo Economico (utils/economiaCommessa.js)
  // W451: varianti accettate dal vivo (stesse regole del Controllo Economico, utils/varianti.js);
  // se non si possono leggere, quelle salvate sul budget
  const rVarianti = dati.varianti ? riepilogoVarianti(dati.varianti) : null;
  const totaleVarianti = rVarianti ? rVarianti.totale : variantiDaBudget(budget);
  const contratto = importoCommessa(budget);
  const valoreCommessa = contratto + totaleVarianti;
  // W453: totali con IL calcolo comune (utils/economiaCommessa.js, totaliCommessa), lo stesso del
  // Controllo Economico. Nel periodo la manodopera è quella delle ore del periodo (W452).
  const fonteOre = dati.fonteId || budget?.costiCalcolati?.fonteOre;
  const oda = dati.odaReport || [];
  const reportPeriodo = !!(dal && al);
  const manodoperaPeriodo = reportPeriodo ? (dati.manodoperaPeriodo || { totale: 0, persone: [], senzaCosto: [] }) : null;
  const tot = totaliCommessa({ budget, movimenti, costoOda: oda.reduce((t, o) => t + o.importo, 0), fontOre: fonteOre, totaleVarianti,
    costiCalcolati: reportPeriodo ? { fonteOre, manodopera: manodoperaPeriodo.totale, diario: 0, noleggi: 0 } : (dati.costiCalcolatiOra || null) });
  const ricavi = tot.ricavi;
  const costiCalcolati = tot.costiCalcolati;
  const costi = tot.costi;
  const margine = tot.margine;
  // Righe della tabella: sommano esattamente ai costi mostrati
  const righeCosti = [
    ...movimenti.filter(m => contaComeCosto(m, fonteOre)).map(m => ({ data: dataMovimento(m), categoria: m.categoriaLabel || m.categoriaId || "—", descrizione: m.dati?.descrizione || m.dati?.nome || m.descrizione || "—", importo: numIt(m.importo) })),
    ...oda.map(o => ({ data: o.data, categoria: "Materiali — ordine di acquisto", descrizione: `${o.numero}${o.fornitore ? ` · ${o.fornitore}` : ""}`, importo: o.importo })),
    ...(reportPeriodo
      ? manodoperaPeriodo.persone.map(p => ({ data: "", quando: "nel periodo", categoria: "Manodopera", descrizione: `${p.nome} — ${numIt(p.ore).toLocaleString("it-IT")} h × €${fmtEuro(p.costoOrario)}/h`, importo: p.totale }))
      : righeCalcolate(dati.costiCalcolatiOra || budget?.costiCalcolati, !!dati.costiCalcolatiOra)),
  ].sort((a, b) => String(b.data || "").localeCompare(String(a.data || "")));
  const righeRicavi = reportPeriodo ? [] : movimenti.filter(eRicavo).map(m => ({ data: dataMovimento(m), categoria: m.categoriaLabel || m.categoriaId || "—", descrizione: m.dati?.descrizione || m.dati?.nome || m.descrizione || "—", importo: numIt(m.importo) }));

  // ── Calcola se ci sono dati significativi ───────────────────────────────────
  const hasDati = tasks.length > 0 || Object.keys(personaleMap).length > 0 ||
    (dati.giornale||[]).length > 0 || (dati.sopralluoghi||[]).length > 0 ||
    (dati.rapportini||[]).length > 0 || (dati.ddt||[]).length > 0 ||
    (dati.verbali||[]).length > 0 || (dati.eventi||[]).length > 0 ||
    (dati.richiesteComm||[]).length > 0 || (dati.fasi||[]).length > 0 ||
    (dati.nf||[]).length > 0 || (dati.richiesteMat||[]).length > 0 ||
    (dati.rientri||[]).length > 0 || (dati.segnalazioni||[]).length > 0 ||
    (dati.noteSpese||[]).length > 0;

  const sezioneNessunoData = () => !hasDati ? `
    <div style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:10px;padding:24px 28px;margin:20px 0;text-align:center;">
      <div style="font-size:32px;margin-bottom:10px;opacity:.3">📭</div>
      <div style="font-size:14px;font-weight:700;color:#374151;margin-bottom:6px">Nessun dato disponibile per il periodo selezionato</div>
      <div style="font-size:11px;color:#9ca3af;max-width:400px;margin:0 auto">
        Nel periodo <strong>${periodo}</strong> non sono stati trovati dati nelle sezioni selezionate per questa commessa.
        Il report verrà arricchito automaticamente man mano che vengono inseriti dati operativi in Nexiora.
      </div>
    </div>` : "";

  // ── HTML sezioni ────────────────────────────────────────────────────────────

  const sezioneRiepilogo = () => sezioni.includes("riepilogo") && hasDati ? `
    <div class="sezione">
      <h2>Riepilogo generale</h2>
      <div class="kpi-grid">
        <div class="kpi"><div class="kpi-n">${tasks.length}</div><div class="kpi-l">Attività totali</div></div>
        <div class="kpi kpi-green"><div class="kpi-n">${completate.length}</div><div class="kpi-l">Completate</div></div>
        <div class="kpi kpi-blue"><div class="kpi-n">${inCorso.length}</div><div class="kpi-l">Da completare</div></div>
        <div class="kpi kpi-red"><div class="kpi-n">${scadute.length}</div><div class="kpi-l">Scadute</div></div>
        <div class="kpi kpi-purple"><div class="kpi-n">${progresso}%</div><div class="kpi-l">Avanzamento</div></div>
      </div>
      <div class="progress-bar-wrap">
        <div class="progress-bar-inner" style="width:${progresso}%"></div>
      </div>
      <p style="text-align:right;font-size:10px;color:#9ca3af;margin-top:2px">Avanzamento: ${progresso}% (${completate.length}/${tasks.length} attività)</p>
      ${noteIntro ? `<div class="nota-box"><strong>Note introduttive:</strong> ${noteIntro}</div>` : ""}
    </div>` : "";

  const statoLabel = t => {
    if(t.status==="completed"||t.status==="completata") return '<span class="badge green">Completata</span>';
    if(t.status==="annullata") return '<span class="badge gray">Annullata</span>';
    if(t.status==="sospesa") return '<span class="badge gray">Sospesa</span>';
    if(isTaskOverdue(t)) return '<span class="badge red">Scaduta</span>';
    if(t.status==="in_progress"||t.status==="in_corso") return '<span class="badge blue">In corso</span>';
    return '<span class="badge gray">Aperta</span>';
  };

  const sezioneAttivita = () => {
    if (!sezioni.includes("attivita") || taskPeriodo.length === 0) return "";
    const tCompletate = taskPeriodo.filter(t=>t.status==="completed"||t.status==="completata");
    const tInCorso = taskPeriodo.filter(t=>!chiusa(t)&&t.status!=="todo"&&t.status!=="aperta");
    const tAperte = taskPeriodo.filter(t=>t.status==="todo"||t.status==="aperta");
    const oggi = toLocalDateStr(new Date());
    const tScadute = taskPeriodo.filter(isTaskOverdue);
    const righeScadute = tScadute.slice().sort((a,b)=>String(a.dueDate).localeCompare(String(b.dueDate))).map(t=>`
      <tr>
        <td>${esc(nomeAttivita(t))}</td>
        <td>${esc(responsabileAttivita(t))}</td>
        <td style="text-align:center;white-space:nowrap;color:#dc2626;font-weight:700">${fmtData(t.dueDate)}</td>
      </tr>`).join("");
    const completatePeriodo = tCompletate.slice().sort((a,b)=>String(dataCompletamento(b)||"").localeCompare(String(dataCompletamento(a)||""))).slice(0,30);
    const righeCompletate = completatePeriodo.map(t=>`
      <tr>
        <td>${esc(nomeAttivita(t))}</td>
        <td>${esc(responsabileAttivita(t))}</td>
        <td style="text-align:center;white-space:nowrap">${dataCompletamento(t) ? fmtData(dataCompletamento(t)) : "—"}</td>
      </tr>`).join("");
    return `<div class="sezione">
      <h2>Attività${dal&&al?` del periodo (${fmtData(dal)} — ${fmtData(al)})`:""}
        <span class="count">${taskPeriodo.length} totali</span></h2>
      <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:${tScadute.length>0?14:0}px;">
        <div style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:10px;text-align:center;">
          <div style="font-size:22px;font-weight:900;color:#1e1b4b">${taskPeriodo.length}</div>
          <div style="font-size:9px;color:#6b7280;font-weight:700;text-transform:uppercase;margin-top:3px">Totali</div>
        </div>
        <div style="background:#f0fdf4;border:1px solid #86efac;border-radius:8px;padding:10px;text-align:center;">
          <div style="font-size:22px;font-weight:900;color:#059669">${tCompletate.length}</div>
          <div style="font-size:9px;color:#059669;font-weight:700;text-transform:uppercase;margin-top:3px">Completate</div>
        </div>
        <div style="background:#eff6ff;border:1px solid #93c5fd;border-radius:8px;padding:10px;text-align:center;">
          <div style="font-size:22px;font-weight:900;color:#2563eb">${tInCorso.length+tAperte.length}</div>
          <div style="font-size:9px;color:#2563eb;font-weight:700;text-transform:uppercase;margin-top:3px">In corso / Aperte</div>
        </div>
        <div style="background:${tScadute.length>0?"#fef2f2":"#f9fafb"};border:1px solid ${tScadute.length>0?"#fca5a5":"#e5e7eb"};border-radius:8px;padding:10px;text-align:center;">
          <div style="font-size:22px;font-weight:900;color:${tScadute.length>0?"#dc2626":"#9ca3af"}">${tScadute.length}</div>
          <div style="font-size:9px;color:${tScadute.length>0?"#dc2626":"#9ca3af"};font-weight:700;text-transform:uppercase;margin-top:3px">Scadute</div>
        </div>
      </div>
      ${tCompletate.length>0?`
      <div style="margin-top:8px">
        <div style="font-size:10px;font-weight:700;color:#059669;margin-bottom:6px">✅ Attività completate nel periodo:</div>
        <table style="width:100%;border-collapse:collapse;font-size:10px;">
          <thead><tr>
            <th style="background:#059669;color:#fff;padding:6px 8px;text-align:left">Attività</th>
            <th style="background:#059669;color:#fff;padding:6px 8px;text-align:left">Responsabile</th>
            <th style="background:#059669;color:#fff;padding:6px 8px;text-align:center;width:90px">Completata il</th>
          </tr></thead>
          <tbody>${righeCompletate}</tbody>
        </table>
        ${tCompletate.length>30?`<div style="font-size:9px;color:#9ca3af;margin-top:4px;font-style:italic">Mostrate le prime 30 su ${tCompletate.length} totali.</div>`:""}
      </div>`:""}
      ${tScadute.length>0?`
      <div style="margin-top:8px">
        <div style="font-size:10px;font-weight:700;color:#dc2626;margin-bottom:6px">⚠️ Attività scadute da completare:</div>
        <table style="width:100%;border-collapse:collapse;font-size:10px;">
          <thead><tr>
            <th style="background:#dc2626;color:#fff;padding:6px 8px;text-align:left">Attività</th>
            <th style="background:#dc2626;color:#fff;padding:6px 8px;text-align:left">Responsabile</th>
            <th style="background:#dc2626;color:#fff;padding:6px 8px;text-align:center;width:90px">Scadenza</th>
          </tr></thead>
          <tbody>${righeScadute}</tbody>
        </table>
      </div>`:""}
      <div style="font-size:10px;color:#9ca3af;margin-top:8px;font-style:italic">
        Il dettaglio completo delle attività è disponibile nella commessa.
      </div>
    </div>`;
  };

  const sezioneCrono = () => {
    const fasi = dati.fasi || [];
    if (!sezioni.includes("cronoprogramma") || fasi.length === 0) return "";
    // W451: le fasi hanno «inizio» e «fine» (prima il report cercava dataInizio/dataFine e
    // mostrava trattini); durata e stato si ricavano dalle date, come nel Cronoprogramma
    const oggiC = toLocalDateStr(new Date());
    const giorniTra = (a, b) => (a && b) ? Math.round((new Date(b + "T00:00:00") - new Date(a + "T00:00:00")) / 86400000) + 1 : null;
    const statoFase = (i, f) => !i || !f ? "—" : f < oggiC ? "Conclusa" : i > oggiC ? "Da iniziare" : "In corso";
    const righe = fasi.map(f=>{
      const i = f.inizio || f.dataInizio || "", fi = f.fine || f.dataFine || "";
      const gg = giorniTra(i, fi);
      return `
      <tr>
        <td><strong>${esc(f.nome||"—")}</strong></td>
        <td>${fmtData(i)}</td>
        <td>${fmtData(fi)}</td>
        <td>${gg ? `${gg} gg` : "—"}</td>
        <td>${f.stato ? esc(f.stato) : statoFase(i, fi)}</td>
      </tr>`; }).join("");
    return `<div class="sezione">
      <h2>Cronoprogramma <span class="count">${fasi.length} fasi</span></h2>
      <table>
        <thead><tr><th>Fase</th><th>Inizio</th><th>Fine</th><th>Durata</th><th>Stato</th></tr></thead>
        <tbody>${righe}</tbody>
      </table>
    </div>`;
  };

  const sezionePersonale = () => {
    const nomi = Object.keys(personaleMap);
    if (!sezioni.includes("personale") || nomi.length === 0) return "";
    const righe = nomi.map(n=>`<tr><td>${n}</td><td style="text-align:right">${personaleMap[n].toFixed(1)}</td></tr>`).join("");
    return `<div class="sezione">
      <h2>Personale e ore <span class="count">${oreTotal.toFixed(1)} h totali</span></h2>
      <table>
        <thead><tr><th>Nominativo</th><th style="text-align:right">Ore imputate</th></tr></thead>
        <tbody>${righe}</tbody>
        <tfoot><tr><td><strong>TOTALE</strong></td><td style="text-align:right"><strong>${oreTotal.toFixed(1)} h</strong></td></tr></tfoot>
      </table>
      ${dati.fonteOre?`<p class="nota-small">Fonte delle ore: ${esc(dati.fonteOre)}, la stessa del Controllo Economico.</p>`:""}
    </div>`;
  };

  const sezioneGiornale = () => {
    const giorni = dati.giornale || [];
    if (!sezioni.includes("giornale") || giorni.length === 0) return "";
    // Mostra solo l'ultima registrazione — il dettaglio è nel giornale di cantiere
    const ultimo = giorni[giorni.length - 1];
    return `<div class="sezione">
      <h2>Giornale di cantiere <span class="count">${giorni.length} registrazioni nel periodo</span></h2>
      <div style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:12px 16px;display:flex;gap:24px;flex-wrap:wrap;align-items:center;">
        <div>
          <div style="font-size:8px;color:#9ca3af;font-weight:700;text-transform:uppercase;margin-bottom:3px">Ultima registrazione</div>
          <div style="font-size:14px;font-weight:800;color:#1e1b4b">${fmtData(ultimo.data)}</div>
        </div>
        ${ultimo.descrizioneAttivita||ultimo.note ? `
        <div style="flex:1;min-width:200px;">
          <div style="font-size:8px;color:#9ca3af;font-weight:700;text-transform:uppercase;margin-bottom:3px">Attività</div>
          <div style="font-size:11px;color:#374151">${(ultimo.descrizioneAttivita||ultimo.note||"").substring(0,150)}${(ultimo.descrizioneAttivita||ultimo.note||"").length>150?"...":""}</div>
        </div>` : ""}
        ${ultimo.operaiPresenti ? `
        <div>
          <div style="font-size:8px;color:#9ca3af;font-weight:700;text-transform:uppercase;margin-bottom:3px">Operai</div>
          <div style="font-size:13px;font-weight:700;color:#374151">${ultimo.operaiPresenti}</div>
        </div>` : ""}
        ${ultimo.meteoCielo ? `
        <div>
          <div style="font-size:8px;color:#9ca3af;font-weight:700;text-transform:uppercase;margin-bottom:3px">Meteo</div>
          <div style="font-size:13px;color:#374151">${ultimo.meteoCielo}</div>
        </div>` : ""}
      </div>
      <div style="font-size:10px;color:#9ca3af;margin-top:8px;font-style:italic">
        Il giornale di cantiere completo è disponibile nella sezione dedicata della commessa.
      </div>
    </div>`;
  };

  const sezioneSopralluoghi = () => {
    const sop = dati.sopralluoghi || [];
    const rapp = dati.rapportini || [];
    if (!sezioni.includes("sopralluoghi") || (sop.length===0&&rapp.length===0)) return "";
    const righeS = sop.map(s=>`
      <tr>
        <td style="font-family:monospace;font-weight:700;color:#7c3aed">${s.numero||"—"}</td>
        <td>Sopralluogo</td>
        <td>${fmtData(s.data)}</td>
        <td>${s.oggetto||s.descrizione||"—"}</td>
        <td>${s.esito||"—"}</td>
      </tr>`).join("");
    const righeR = rapp.map(r=>`
      <tr>
        <td style="font-family:monospace;font-weight:700;color:#059669">${r.numero||"—"}</td>
        <td>Intervento</td>
        <td>${fmtData(r.data)}</td>
        <td>${r.motivoIntervento||r.titoloIntervento||r.descrizione||"—"}</td>
        <td>${r.esitoIntervento||"—"}</td>
      </tr>`).join("");
    return `<div class="sezione">
      <h2>Sopralluoghi e interventi <span class="count">${sop.length+rapp.length} doc.</span></h2>
      <table>
        <thead><tr><th>N°</th><th>Tipo</th><th>Data</th><th>Oggetto</th><th>Esito</th></tr></thead>
        <tbody>${righeS}${righeR}</tbody>
      </table>
    </div>`;
  };

  const sezioneDDT = () => {
    const ddts = dati.ddt || [];
    if (!sezioni.includes("ddt") || ddts.length === 0) return "";
    const righe = ddts.map(d=>`
      <tr>
        <td style="font-family:monospace;font-weight:700;color:#7c3aed">${d.numero||"—"}</td>
        <td>${fmtData(d.dataDocumento)}</td>
        <td>${d.destinatarioNome||"—"}</td>
        <td>${d.causale||"—"}</td>
        <td>${d.nColli||"—"}</td>
      </tr>`).join("");
    return `<div class="sezione">
      <h2>DDT – Documenti di trasporto <span class="count">${ddts.length}</span></h2>
      <table>
        <thead><tr><th>N° DDT</th><th>Data</th><th>Destinatario</th><th>Causale</th><th>Colli</th></tr></thead>
        <tbody>${righe}</tbody>
      </table>
    </div>`;
  };

  const sezioneMateriali = () => {
    const nf = dati.nf || [];
    const rm = dati.richiesteMat || [];
    if (!sezioni.includes("materiali") || (nf.length===0 && rm.length===0)) return "";
    const righeNF = nf.map(n=>`
      <tr>
        <td style="font-family:monospace;font-weight:700;color:#7c3aed">${n.numero?`NF-${n.numero}`:"—"}</td>
        <td>${fmtData(n.dataRichiesta)}</td>
        <td style="white-space:pre-line">${n.descrizione||"—"}</td>
        <td>${n.fornitoreId?(n.fornitoreNome||"—"):"N.D."}</td>
        <td>${statoNf(n)}${haOdaValido(n)?` (${n.odaNumero||"ODA"})`:""}</td>
      </tr>`).join("");
    const righeRM = rm.map(r=>`
      <tr>
        <td style="font-family:monospace;font-weight:700;color:#0891b2">RICH</td>
        <td>${fmtDataOra(r.createdAt).split(",")[0]||"—"}</td>
        <td>${(r.righe||[]).map(x=>x.descrizione).filter(Boolean).join(", ")||"—"}</td>
        <td>Magazzino interno</td>
        <td>${r.stato||"—"}</td>
      </tr>`).join("");
    return `<div class="sezione">
      <h2>Materiali — NF e richieste al magazzino <span class="count">${nf.length+rm.length}</span></h2>
      <table>
        <thead><tr><th>Rif.</th><th>Data</th><th>Descrizione</th><th>Fornitore/Origine</th><th>Stato</th></tr></thead>
        <tbody>${righeNF}${righeRM}</tbody>
      </table>
    </div>`;
  };

  const sezioneRientroMateriali = () => {
    const rientri = dati.rientri || [];
    if (!sezioni.includes("rientroMateriali") || rientri.length === 0) return "";
    const righe = rientri.map(r=>`
      <tr>
        <td style="font-family:monospace;font-weight:700;color:#7c3aed">${r.numero||"—"}</td>
        <td>${fmtData(r.dataRientro)}</td>
        <td>${r.descrizione||"—"}</td>
        <td style="text-align:right">${r.quantita||"—"} ${r.unitaMisura||""}</td>
        <td>${r.valoreNoto?`-€${fmtEuro(r.quantita*r.prezzoUnitarioApplicato)}`:"n.d."}</td>
      </tr>`).join("");
    return `<div class="sezione">
      <h2>Rientro Materiali <span class="count">${rientri.length}</span></h2>
      <table>
        <thead><tr><th>N°</th><th>Data</th><th>Materiale</th><th style="text-align:right">Quantità</th><th>Valore scalato</th></tr></thead>
        <tbody>${righe}</tbody>
      </table>
    </div>`;
  };

  const sezioneVerbali = () => {
    const verb = dati.verbali || [];
    if (!sezioni.includes("verbali") || verb.length === 0) return "";
    const righe = verb.map(v=>`
      <tr>
        <td>${fmtData(v.data)}</td>
        <td>${v.titolo||v.oggetto||"—"}</td>
        <td>${v.luogo||"—"}</td>
        <td>${v.compilatoreNome||"—"}</td>
      </tr>`).join("");
    return `<div class="sezione">
      <h2>Verbali di riunione <span class="count">${verb.length}</span></h2>
      <table>
        <thead><tr><th>Data</th><th>Oggetto</th><th>Luogo</th><th>Redatto da</th></tr></thead>
        <tbody>${righe}</tbody>
      </table>
    </div>`;
  };

  const sezioneEventi = () => {
    const ev = dati.eventi || [];
    if (!sezioni.includes("eventi") || ev.length === 0) return "";
    // Mostra i 10 più recenti — un report di sintesi non deve elencarli tutti, ma 3 era troppo poco
    const recenti = [...ev].sort((a,b)=>(dataEvento(b)).localeCompare(dataEvento(a))).slice(0,10);
    const righe = recenti.map(e=>`
      <tr>
        <td style="white-space:nowrap">${fmtData(dataEvento(e))}${e.oraInizio?` ${esc(e.oraInizio)}`:""}</td>
        <td style="font-weight:600">${esc(e.titolo||e.nome||"—")}</td>
        <td style="font-size:10px;color:#6b7280">${esc(e.descrizione||"—")}</td>
      </tr>`).join("");
    return `<div class="sezione">
      <h2>Eventi significativi <span class="count">${ev.length} nel periodo</span></h2>
      <table>
        <thead><tr><th>Data</th><th>Evento</th><th>Note</th></tr></thead>
        <tbody>${righe}</tbody>
      </table>
      ${ev.length>10?`<div style="font-size:10px;color:#9ca3af;margin-top:8px;font-style:italic">Mostrati i 10 più recenti su ${ev.length} totali. Il calendario completo è disponibile nella commessa.</div>`:""}
    </div>`;
  };

  const sezioneCriticita = () => {
    if (!sezioni.includes("criticita")) return "";
    const scad = scadute.length;
    const aperte = inCorso.filter(t=>t.priorita==="alta"||t.priority==="high");
    const anomalie = (dati.sopralluoghi||[]).flatMap(s=>s.anomalie||[]).filter(a=>a.stato!=="risolta");
    if (scad===0 && aperte.length===0 && anomalie.length===0) return "";

    const righeScadute = scadute.slice(0,20).map(t=>`
      <tr>
        <td style="padding:5px 8px;border-bottom:1px solid #f3f4f6">${esc(nomeAttivita(t))}</td>
        <td style="padding:5px 8px;border-bottom:1px solid #f3f4f6;text-align:center;white-space:nowrap">${fmtData(t.dueDate)}</td>
        <td style="padding:5px 8px;border-bottom:1px solid #f3f4f6">${esc(responsabileAttivita(t))}</td>
      </tr>`).join("");

    const righeAperte = aperte.slice(0,20).map(t=>`
      <tr>
        <td style="padding:5px 8px;border-bottom:1px solid #f3f4f6">${esc(nomeAttivita(t))}</td>
        <td style="padding:5px 8px;border-bottom:1px solid #f3f4f6;text-align:center;white-space:nowrap">${fmtData(t.dueDate)}</td>
        <td style="padding:5px 8px;border-bottom:1px solid #f3f4f6">${esc(responsabileAttivita(t))}</td>
      </tr>`).join("");

    const righeAnomalie = anomalie.slice(0,20).map(a=>`
      <tr>
        <td style="padding:5px 8px;border-bottom:1px solid #f3f4f6">${a.descrizione||a.titolo||"—"}</td>
        <td style="padding:5px 8px;border-bottom:1px solid #f3f4f6;text-align:center">
          <span style="padding:2px 8px;border-radius:10px;font-size:9px;font-weight:700;background:#fef3c7;color:#92400e">${a.gravita||"—"}</span>
        </td>
        <td style="padding:5px 8px;border-bottom:1px solid #f3f4f6">${a.azioneCorrettiva||"—"}</td>
      </tr>`).join("");

    return `<div class="sezione">
      <h2>Criticità e anomalie</h2>

      ${scad>0?`
      <div class="alert-box red" style="margin-bottom:10px"><strong>⚠️ ${scad} attività scadute</strong> non ancora completate</div>
      <table style="width:100%;border-collapse:collapse;font-size:10px;margin-bottom:14px;">
        <thead><tr>
          <th style="background:#dc2626;color:#fff;padding:6px 8px;text-align:left">Attività</th>
          <th style="background:#dc2626;color:#fff;padding:6px 8px;text-align:center;width:90px">Scadenza</th>
          <th style="background:#dc2626;color:#fff;padding:6px 8px;text-align:left">Responsabile</th>
        </tr></thead>
        <tbody>${righeScadute}</tbody>
      </table>`:""}

      ${aperte.length>0?`
      <div class="alert-box yellow" style="margin-bottom:10px"><strong>⚡ ${aperte.length} attività ad alta priorità</strong> ancora in corso</div>
      <table style="width:100%;border-collapse:collapse;font-size:10px;margin-bottom:14px;">
        <thead><tr>
          <th style="background:#d97706;color:#fff;padding:6px 8px;text-align:left">Attività</th>
          <th style="background:#d97706;color:#fff;padding:6px 8px;text-align:center;width:90px">Scadenza</th>
          <th style="background:#d97706;color:#fff;padding:6px 8px;text-align:left">Responsabile</th>
        </tr></thead>
        <tbody>${righeAperte}</tbody>
      </table>`:""}

      ${anomalie.length>0?`
      <div class="alert-box yellow" style="margin-bottom:10px"><strong>🔍 ${anomalie.length} anomalie</strong> rilevate nei sopralluoghi non ancora risolte</div>
      <table style="width:100%;border-collapse:collapse;font-size:10px;margin-bottom:6px;">
        <thead><tr>
          <th style="background:#92400e;color:#fff;padding:6px 8px;text-align:left">Descrizione anomalia</th>
          <th style="background:#92400e;color:#fff;padding:6px 8px;text-align:center;width:80px">Gravità</th>
          <th style="background:#92400e;color:#fff;padding:6px 8px;text-align:left">Azione correttiva</th>
        </tr></thead>
        <tbody>${righeAnomalie}</tbody>
      </table>`:""}

    </div>`;
  };

  const sezioneProssime = () => {
    if (!sezioni.includes("prossime") || isChiusura) return "";
    const oggi = toLocalDateStr(new Date());
    const future = tasks.filter(t=>t.dueDate&&t.dueDate>oggi&&!chiusa(t))
      .sort((a,b)=>a.dueDate.localeCompare(b.dueDate)).slice(0,10);
    if (future.length===0) return "";
    const righe = future.map(t=>`
      <tr>
        <td>${esc(nomeAttivita(t))}</td>
        <td>${fmtData(t.dueDate)}</td>
        <td>${esc(responsabileAttivita(t))}</td>
      </tr>`).join("");
    return `<div class="sezione">
      <h2>Prossime attività <span class="count">${future.length}</span></h2>
      <table>
        <thead><tr><th>Attività</th><th>Scadenza</th><th>Responsabile</th></tr></thead>
        <tbody>${righe}</tbody>
      </table>
    </div>`;
  };

  const sezioneSegnalazioni = () => {
    const seg = dati.segnalazioni || [];
    if (!sezioni.includes("segnalazioni") || seg.length === 0) return "";
    const righe = seg.map(s=>`
      <tr>
        <td style="font-family:monospace;font-weight:700;color:#7c3aed">${s.numero||"—"}</td>
        <td>${fmtData(s.dataRicezione)}</td>
        <td>${s.canale||"—"}</td>
        <td>${(s.descrizione||"—").substring(0,80)}${(s.descrizione||"").length>80?"...":""}</td>
        <td>${s.stato==="chiusa"?"Chiusa":s.stato==="in_lavorazione"?"In lavorazione":"Nuova"}</td>
      </tr>`).join("");
    return `<div class="sezione">
      <h2>Segnalazioni Cliente <span class="count">${seg.length}</span></h2>
      <table>
        <thead><tr><th>N°</th><th>Data</th><th>Canale</th><th>Richiesta</th><th>Stato</th></tr></thead>
        <tbody>${righe}</tbody>
      </table>
    </div>`;
  };

  const sezioneNoteSpese = () => {
    if (!sezioni.includes("notespese") || isCliente) return "";
    const ns = dati.noteSpese || [];
    if (ns.length===0) return "";
    const righe = ns.map(r=>`
      <tr>
        <td>${fmtData(r.data)}</td>
        <td>${r.dipendenteNome||"—"}</td>
        <td>${r.destinazione||"—"}</td>
        <td style="text-align:right">${r.totale?`€${fmtEuro(r.totale)}`:"—"}</td>
      </tr>`).join("");
    const totNS = ns.reduce((s,r)=>s+(parseFloat(r.totale)||0),0);
    return `<div class="sezione riservata">
      <h2>Note spese <span class="count">${ns.length}</span> <span class="badge-interno">Solo uso interno</span></h2>
      <table>
        <thead><tr><th>Data</th><th>Dipendente</th><th>Destinazione</th><th style="text-align:right">Importo</th></tr></thead>
        <tbody>${righe}</tbody>
        <tfoot><tr><td colspan="3"><strong>TOTALE</strong></td><td style="text-align:right;font-weight:900;color:#dc2626">€${fmtEuro(totNS)}</td></tr></tfoot>
      </table>
    </div>`;
  };

  const sezioneCommerciale = () => {
    if (!sezioni.includes("commerciale") || isCliente) return "";
    const rc = dati.richiesteComm || [];
    if (rc.length===0) return "";
    const righe = rc.map(r=>`
      <tr>
        <td style="font-family:monospace;font-weight:700;color:#d97706">${r.numero||"—"}</td>
        <td>${fmtData(r.dataEmissione)}</td>
        <td>${r.oggetto||"—"}</td>
        <td>${r.stato||"—"}</td>
        <td style="text-align:right;font-weight:700;color:#059669">€${fmtEuro(r.totaleOfferta)}</td>
      </tr>`).join("");
    const totOff = rc.reduce((s,r)=>s+(parseFloat(r.totaleOfferta)||0),0);
    return `<div class="sezione riservata">
      <h2>Inoltro costi per offerta al cliente <span class="count">${rc.length}</span> <span class="badge-interno">Solo uso interno</span></h2>
      <table>
        <thead><tr><th>N°</th><th>Data</th><th>Oggetto</th><th>Stato</th><th>Valore offerta</th></tr></thead>
        <tbody>${righe}</tbody>
        <tfoot><tr><td colspan="4"><strong>TOTALE</strong></td><td style="text-align:right;font-weight:900;color:#059669">€${fmtEuro(totOff)}</td></tr></tfoot>
      </table>
    </div>`;
  };

  const sezioneEconomico = () => {
    if (!sezioni.includes("economico") || isCliente) return "";
    if (!righeCosti.length && !righeRicavi.length && !valoreCommessa) return "";
    const MAX = 40;
    const riga = (r, tipo) => `<tr>
            <td>${r.data ? fmtData(r.data) : (r.quando || "—")}</td>
            <td>${esc(r.categoria)}</td>
            <td>${esc(r.descrizione)}</td>
            <td><span class="badge ${tipo==="ricavo"?"green":"red"}">${tipo}</span></td>
            <td style="text-align:right;font-weight:600">€${fmtEuro(r.importo)}</td>
          </tr>`;
    const senzaCosto = reportPeriodo && manodoperaPeriodo.senzaCosto.length
      ? `<p style="font-size:11px;color:#b45309;margin-top:6px">⚠️ Ore senza costo orario (non entrano nei costi): ${esc(manodoperaPeriodo.senzaCosto.join(", "))}. Si collega da Dipendenti, come nel Controllo Economico.</p>` : "";
    return `<div class="sezione riservata">
      <h2>Sezione economica <span class="badge-interno">Solo uso interno</span></h2>
      ${reportPeriodo ? `
      <div class="kpi-grid">
        <div class="kpi kpi-red"><div class="kpi-n">€${fmtEuro(costi)}</div><div class="kpi-l">Costi del periodo</div></div>
        ${valoreCommessa?`<div class="kpi kpi-blue"><div class="kpi-n">€${fmtEuro(valoreCommessa)}</div><div class="kpi-l">Valore commessa</div>${totaleVarianti?`<div style="font-size:9px;color:#6b7280;margin-top:3px">contratto €${fmtEuro(contratto)} + varianti accettate €${fmtEuro(totaleVarianti)}</div>`:""}</div>`:""}
      </div>
      <p style="font-size:11px;color:#6b7280;margin-top:6px">Report di periodo: costi del periodo IVA esclusa — movimenti, ordini di acquisto e manodopera (${esc(dati.fonteOre || "fonte delle ore dell'azienda")}), con le regole del Controllo Economico. Ricavi e margine si leggono nel report completo della commessa.</p>` : `
      <div class="kpi-grid">
        <div class="kpi kpi-green"><div class="kpi-n">€${fmtEuro(ricavi)}</div><div class="kpi-l">Ricavi</div></div>
        <div class="kpi kpi-red"><div class="kpi-n">€${fmtEuro(costi)}</div><div class="kpi-l">Costi</div></div>
        <div class="kpi ${margine>=0?"kpi-green":"kpi-red"}"><div class="kpi-n">€${fmtEuro(margine)}</div><div class="kpi-l">Margine</div></div>
        ${valoreCommessa?`<div class="kpi kpi-blue"><div class="kpi-n">€${fmtEuro(valoreCommessa)}</div><div class="kpi-l">Valore commessa</div>${totaleVarianti?`<div style="font-size:9px;color:#6b7280;margin-top:3px">contratto €${fmtEuro(contratto)} + varianti accettate €${fmtEuro(totaleVarianti)}</div>`:""}</div>`:""}
      </div>
      <p style="font-size:11px;color:#6b7280;margin-top:6px">Importi IVA esclusa. Costi = movimenti registrati + ordini di acquisto${costiCalcolati?` + manodopera e noleggi ${dati.costiCalcolatiOra ? "calcolati alla generazione del report" : "calcolati all'ultima apertura del Controllo Economico"}`:""}, con le regole del Controllo Economico.</p>`}
      ${senzaCosto}
      ${righeCosti.length || righeRicavi.length ? `
      <table style="margin-top:12px">
        <thead><tr><th>Data</th><th>Categoria</th><th>Descrizione</th><th>Tipo</th><th style="text-align:right">Importo</th></tr></thead>
        <tbody>
          ${righeCosti.slice(0, MAX).map(r => riga(r, "costo")).join("")}
          <tr><td colspan="4" style="text-align:right;font-weight:700">Totale costi${reportPeriodo ? " del periodo" : ""}${righeCosti.length > MAX ? ` (tutte le ${righeCosti.length} voci)` : ""}</td><td style="text-align:right;font-weight:700">€${fmtEuro(costi)}</td></tr>
          ${righeRicavi.map(r => riga(r, "ricavo")).join("")}
        </tbody>
      </table>
      ${righeCosti.length > MAX ? `<p style="font-size:11px;color:#6b7280">Elencate le prime ${MAX} voci di costo su ${righeCosti.length}; il totale le comprende tutte.</p>` : ""}` : ""}
    </div>`;
  };

  const periodo = isChiusura
    ? `${fmtData(toLocalDateStr(projectData?.createdAt?.toDate?.()))} — ${fmtData(projectData?.dataChiusura)}`
    : `${fmtData(dal)} — ${fmtData(al)}`;

  win.document.write(`<!DOCTYPE html><html lang="it"><head>
  <meta charset="utf-8"/>
  <title>${nomeFile}</title>
  <style>
    *{margin:0;padding:0;box-sizing:border-box;}
    body{font-family:Arial,Helvetica,sans-serif;font-size:11px;color:#111;background:#fff;}
    @page{size:A4 portrait;margin:15mm 16mm 18mm;}
    /* A schermo: foglio A4 centrato; in stampa i margini li dà la pagina */
    @media screen{
      html,body{background:#e5e7eb;}
      .foglio{width:210mm;margin:28px auto;background:#fff;padding:16mm 16mm 12mm;box-shadow:0 4px 24px rgba(17,24,39,.12);border-radius:4px;}
      .cover{min-height:250mm;}
    }
    @media screen and (max-width:860px){.foglio{width:auto;margin:0;padding:18px 16px;border-radius:0;box-shadow:none;}.cover{min-height:0;}}
    @media print{
      .sezione{page-break-inside:avoid;}
      table{page-break-inside:auto;}
      tr{page-break-inside:avoid;page-break-after:auto;}
      .cover{page-break-after:always;min-height:262mm;}
    }
    /* COPERTINA — carta intestata in alto, titolo e dati della commessa centrati */
    .cover{display:flex;flex-direction:column;}
    .cover-top{display:grid;grid-template-columns:1fr auto;gap:20px;align-items:start;border-bottom:3px solid ${coloreDoc};padding-bottom:12px;}
    .cover-top.centro{grid-template-columns:1fr;text-align:center;}
    .cover-top.destra>div:first-child{order:2;text-align:right;}
    .cover-centro{flex:1;display:flex;flex-direction:column;justify-content:center;align-items:center;text-align:center;padding:30px 0;}
    .cover-tipo{font-size:11px;font-weight:700;color:#7c3aed;text-transform:uppercase;letter-spacing:.12em;margin-bottom:10px;}
    .cover-numero{font-size:15px;font-weight:900;color:#5b21b6;font-family:'Courier New',monospace;letter-spacing:.5px;margin-bottom:18px;padding:4px 14px;border:1.5px solid #ddd6fe;border-radius:8px;background:#faf5ff;}
    .cover-titolo{font-size:28px;font-weight:900;color:#1e1b4b;line-height:1.2;margin-bottom:6px;max-width:150mm;}
    .cover-sub{font-size:15px;color:#4b5563;margin-bottom:18px;}
    .cover-info{display:grid;grid-template-columns:1fr 1fr;gap:14px 30px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:10px;padding:18px 28px;margin:18px auto 0;width:100%;max-width:150mm;text-align:left;}
    .info-item .lbl{font-size:8.5px;color:#6b7280;font-weight:700;text-transform:uppercase;letter-spacing:.05em;display:block;margin-bottom:3px;}
    .info-item .val{font-size:13px;font-weight:700;color:#1e1b4b;}
    .cover-versione{display:inline-block;padding:5px 14px;border-radius:20px;font-size:11px;font-weight:700;
      background:${isCliente?"#dbeafe":"#fef3c7"};color:${isCliente?"#1d4ed8":"#92400e"};}
    .cover-bottom{border-top:1px solid #e5e7eb;padding-top:8px;display:flex;justify-content:space-between;gap:10px;font-size:9.5px;color:#6b7280;}
    .cover-legale{white-space:pre-line;text-align:center;font-size:8.5px;color:#6b7280;margin-bottom:6px;}
    /* CONTENUTO */
    .sezione{margin-top:24px;padding-bottom:14px;border-bottom:1px solid #f3f4f6;}
    .contenuto{counter-reset:sez;}
    .contenuto .sezione>h2::before{counter-increment:sez;content:counter(sez) ".";margin-right:2px;}
    .sezione:last-child{border-bottom:none;}
    h2{font-size:14px;font-weight:800;color:#1e1b4b;margin-bottom:12px;display:flex;align-items:center;gap:8px;padding:7px 12px;background:#f9fafb;border-left:4px solid #7c3aed;border-radius:0 6px 6px 0;}
    .count{font-size:10px;font-weight:700;background:#7c3aed;color:#fff;padding:2px 8px;border-radius:10px;margin-left:4px;}
    table{width:100%;border-collapse:collapse;font-size:10px;}
    th{background:#1e1b4b;color:#fff;padding:6px 8px;text-align:left;font-size:9px;font-weight:700;text-transform:uppercase;}
    td{padding:6px 8px;border-bottom:1px solid #f3f4f6;}
    tr:nth-child(even) td{background:#fafafa;}
    tfoot td{background:#f3f4f6!important;border-top:2px solid #e5e7eb;}
    .badge{display:inline-block;padding:1px 7px;border-radius:10px;font-size:9px;font-weight:700;}
    .badge.green{background:#d1fae5;color:#065f46;}
    .badge.red{background:#fee2e2;color:#991b1b;}
    .badge.blue{background:#dbeafe;color:#1d4ed8;}
    .badge.gray{background:#f3f4f6;color:#6b7280;}
    .badge-interno{font-size:9px;padding:2px 8px;border-radius:10px;background:#fef3c7;color:#92400e;font-weight:700;}
    .kpi-grid{display:grid;grid-template-columns:repeat(5,1fr);gap:10px;margin-bottom:12px;}
    .kpi{background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:10px;text-align:center;}
    .kpi-green{border-color:#86efac;background:#f0fdf4;}
    .kpi-red{border-color:#fca5a5;background:#fef2f2;}
    .kpi-blue{border-color:#93c5fd;background:#eff6ff;}
    .kpi-purple{border-color:#c4b5fd;background:#faf5ff;}
    .kpi-n{font-size:20px;font-weight:900;color:#1e1b4b;}
    .kpi-l{font-size:9px;color:#6b7280;font-weight:700;text-transform:uppercase;margin-top:3px;}
    .progress-bar-wrap{background:#e5e7eb;border-radius:10px;height:8px;overflow:hidden;}
    .progress-bar-inner{background:linear-gradient(90deg,#7c3aed,#a78bfa);height:100%;border-radius:10px;transition:width .3s;}
    .nota-box{background:#fef3c7;border:1px solid #fde68a;border-radius:6px;padding:8px 12px;margin-top:10px;font-size:10px;}
    .nota-small{font-size:9px;color:#9ca3af;margin-top:6px;}
    .alert-box{border-radius:6px;padding:8px 12px;margin-bottom:6px;font-size:11px;}
    .alert-box.red{background:#fef2f2;border:1px solid #fca5a5;color:#dc2626;}
    .alert-box.yellow{background:#fef3c7;border:1px solid #fde68a;color:#92400e;}
    .riservata h2{border-left-color:#d97706;}
    .riservata h2 .count{background:#d97706;}
    .footer{border-top:1px solid #e5e7eb;padding-top:8px;font-size:9px;color:#9ca3af;display:flex;justify-content:space-between;margin-top:20px;}
    ${isCliente?".riservata{display:none!important;}":""}
  </style>
  </head><body>

  <div class="foglio">
  <!-- COPERTINA -->
  <div class="cover">
    <div class="cover-top ${posizioneLogo}">
      <div>${logoHtml}
        <div style="font-size:13px;font-weight:800;color:${coloreDoc};margin-top:4px">${escR(orgInfo?.nome||orgInfo?.name||"")}</div>
        <div style="font-size:9.5px;color:#4b5563;margin-top:2px;line-height:1.55">
          ${[escR(orgInfo?.indirizzo||""), [orgInfo?.piva?`P.IVA ${escR(orgInfo.piva)}`:"", orgInfo?.codiceFiscale&&orgInfo.codiceFiscale!==orgInfo.piva?`C.F. ${escR(orgInfo.codiceFiscale)}`:""].filter(Boolean).join(" · "), [orgInfo?.telefono?`Tel. ${escR(orgInfo.telefono)}`:"", escR(orgInfo?.email||"")].filter(Boolean).join(" · ")].filter(Boolean).join("<br/>")}
        </div>
      </div>
      <div style="text-align:${posizioneLogo==="centro"?"center":posizioneLogo==="destra"?"left":"right"}">
        <div style="font-size:10px;color:#6b7280">Data di emissione</div>
        <div style="font-size:12px;font-weight:700">${new Date().toLocaleDateString("it-IT",{day:"numeric",month:"long",year:"numeric"})}</div>
      </div>
    </div>

    <div class="cover-centro">
      <div class="cover-tipo">${isChiusura?"Fascicolo riepilogativo di commessa":"Report periodico di commessa"}</div>
      ${numero?`<div class="cover-numero">${escR(numero)}</div>`:""}
      <div class="cover-titolo">${escR(projectData?.name||"—")}</div>
      <div class="cover-sub">${escR(projectData?.clienteNome||"")}</div>
      <span class="cover-versione">Versione ${isCliente?"Cliente":"Interna"}</span>
      <div class="cover-info">
        <div class="info-item"><span class="lbl">Codice commessa</span><span class="val">${escR(projectData?.codiceCommessa||"—")}</span></div>
        <div class="info-item"><span class="lbl">Cliente</span><span class="val">${escR(projectData?.clienteNome||"—")}</span></div>
        <div class="info-item"><span class="lbl">Periodo</span><span class="val">${periodo}</span></div>
        <div class="info-item"><span class="lbl">Responsabile</span><span class="val">${escR(projectData?.responsabileNome||"—")}</span></div>
        <div class="info-item"><span class="lbl">Stato commessa</span><span class="val">${projectData?.statoCommessa==="chiusa"?"🔒 Chiusa":"🟢 Attiva"}</span></div>
        <div class="info-item"><span class="lbl">Redatto da</span><span class="val">${escR(currentUser?.displayName||"—")}</span></div>
      </div>
    </div>

    ${orgInfo?.piePagina?`<div class="cover-legale">${escR(orgInfo.piePagina)}</div>`:""}
    <div class="cover-bottom">
      <span>${escR(numero||"")}</span>
      <span>Generato con Nexiora — ${dominioSito()}</span>
      <span>${new Date().toLocaleString("it-IT")}</span>
    </div>
  </div>

  <!-- CONTENUTO -->
  <div class="contenuto" style="margin-top:20px">
    ${sezioneNessunoData()}
    ${sezioneRiepilogo()}
    ${sezioneAttivita()}
    ${sezioneCrono()}
    ${sezionePersonale()}
    ${sezioneGiornale()}
    ${sezioneSopralluoghi()}
    ${sezioneDDT()}
    ${sezioneMateriali()}
    ${sezioneRientroMateriali()}
    ${sezioneVerbali()}
    ${sezioneEventi()}
    ${sezioneCriticita()}
    ${sezioneProssime()}
    ${sezioneSegnalazioni()}
    ${sezioneCommerciale()}
    ${sezioneNoteSpese()}
    ${sezioneEconomico()}
    ${noteFinale?`<div class="sezione"><h2>Note conclusive</h2><div class="nota-box">${noteFinale}</div></div>`:""}
  </div>

  <div class="footer">
    <span>${numero||""} — ${TIPI_REPORT[tipo].label}</span>
    <span>${projectData?.name}</span>
    <span>Nexiora — ${dominioSito()}</span>
  </div>
  </div>
  </body></html>`);
  win.document.close();
    setTimeout(()=>{
      if (win.closed) return;
      const style = win.document.createElement("style");
      style.textContent = "@media print { .no-print-btn { display:none !important; } }";
      win.document.head.appendChild(style);
      const btn = win.document.createElement("button");
      btn.textContent = "🖨️ Stampa";
      btn.className = "no-print-btn";
      btn.onclick = () => win.print();
      btn.style.cssText = "position:fixed;top:16px;right:16px;z-index:9999;padding:10px 20px;background:#7c3aed;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;font-family:Arial,sans-serif;box-shadow:0 4px 12px rgba(0,0,0,.2);";
      win.document.body.appendChild(btn);
    }, 50);
}

// ─── Wizard configurazione ────────────────────────────────────────────────────
function WizardReport({ tipo, projectData, orgId, onClose, onGenera }) {
  const { isOrgAdmin, isPlatformOwner } = useAuth();
  const oggi = toLocalDateStr(new Date());
  const primoDelMese = toLocalDateStr(new Date(new Date().getFullYear(), new Date().getMonth(), 1));

  const [versione, setVersione] = useState("interno");
  const [dal, setDal] = useState(tipo==="chiusura" ? (toLocalDateStr(projectData?.createdAt?.toDate?.())||primoDelMese) : primoDelMese);
  const [al, setAl] = useState(tipo==="chiusura" ? (projectData?.dataChiusura||oggi) : oggi);
  const [sezioni, setSezioni] = useState(tipo==="chiusura" ? [...SEZIONI_DEFAULT_CHIUSURA] : [...SEZIONI_DEFAULT_PERIODICO]);
  const [noteIntro, setNoteIntro] = useState("");
  const [noteFinale, setNoteFinale] = useState("");
  const [loading, setLoading] = useState(false);

  const toggleSez = id => {
    const sez = SEZIONI_DISPONIBILI.find(s=>s.id===id);
    if (sez?.riservata && versione==="cliente") return; // blocca riservate in versione cliente
    setSezioni(s => s.includes(id) ? s.filter(x=>x!==id) : [...s,id]);
  };

  // Quando si passa a versione interna, aggiunge automaticamente le sezioni riservate
  useEffect(()=>{
    if(versione==="interno"){
      setSezioni(s=>{
        const sezioniRiservate = SEZIONI_DISPONIBILI.filter(x=>x.riservata).map(x=>x.id);
        const mancanti = sezioniRiservate.filter(id=>!s.includes(id));
        return mancanti.length>0 ? [...s,...mancanti] : s;
      });
    }
  },[versione]);

  const canSeeReservati = isOrgAdmin || isPlatformOwner;

  const inp = {padding:"7px 10px",border:"1px solid #e5e7eb",borderRadius:7,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none",width:"100%",boxSizing:"border-box"};
  const lbl = {fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4};

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
      <div style={{background:"#fff",borderBottom:"1px solid #e5e7eb",padding:"12px 20px",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
        <div>
          <div style={{fontSize:14,fontWeight:800,color:"#1e1b4b"}}>
            {TIPI_REPORT[tipo].emoji} {tipo==="chiusura"?"Configura Report di Chiusura":"Configura Report Periodico"}
          </div>
          <div style={{fontSize:11,color:"#9ca3af",marginTop:2}}>{projectData?.name}</div>
        </div>
        <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer"}}><X size={16}/></button>
      </div>

      <div style={{flex:1,overflowY:"auto",padding:"20px",maxWidth:700,margin:"0 auto",width:"100%"}}>
        {/* Versione */}
        <div style={{background:"#f9fafb",border:"1px solid #e5e7eb",borderRadius:10,padding:16,marginBottom:16}}>
          <div style={{fontSize:12,fontWeight:700,color:"#374151",marginBottom:10}}>📋 Versione report</div>
          <div style={{display:"flex",gap:10}}>
            {[{v:"interno",l:"🏢 Report Interno",desc:"Include costi, margini, richieste commerciali"},{v:"cliente",l:"👤 Report Cliente",desc:"Esclude automaticamente dati riservati"}].map(o=>(
              <button key={o.v} onClick={()=>{setVersione(o.v);if(o.v==="cliente")setSezioni(s=>s.filter(x=>!SEZIONI_DISPONIBILI.find(d=>d.id===x)?.riservata));}}
                style={{flex:1,padding:"10px 14px",border:`2px solid ${versione===o.v?"#7c3aed":"#e5e7eb"}`,borderRadius:8,
                  background:versione===o.v?"#faf5ff":"#fff",cursor:"pointer",textAlign:"left",fontFamily:"'Inter',sans-serif"}}>
                <div style={{fontSize:12,fontWeight:700,color:versione===o.v?"#7c3aed":"#374151"}}>{o.l}</div>
                <div style={{fontSize:10,color:"#9ca3af",marginTop:3}}>{o.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Periodo */}
        {tipo!=="chiusura" && (
          <div style={{background:"#f9fafb",border:"1px solid #e5e7eb",borderRadius:10,padding:16,marginBottom:16}}>
            <div style={{fontSize:12,fontWeight:700,color:"#374151",marginBottom:10}}>📅 Periodo</div>
            <div style={{display:"flex",gap:16,marginBottom:10}}>
              <div style={{flex:1}}><label style={lbl}>Dal *</label><input type="date" style={inp} value={dal} onChange={e=>setDal(e.target.value)}/></div>
              <div style={{flex:1}}><label style={lbl}>Al *</label><input type="date" style={inp} value={al} onChange={e=>setAl(e.target.value)}/></div>
            </div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {[
                {l:"Questa settimana",fn:()=>{const d=new Date();const lun=new Date(d.setDate(d.getDate()-d.getDay()+1));setDal(toLocalDateStr(lun));setAl(oggi);}},
                {l:"Questo mese",fn:()=>{setDal(primoDelMese);setAl(oggi);}},
                {l:"Ultimo trimestre",fn:()=>{const d=new Date();d.setMonth(d.getMonth()-3);setDal(toLocalDateStr(d));setAl(oggi);}},
              ].map(p=>(
                <button key={p.l} onClick={p.fn} style={{padding:"4px 10px",border:"1px solid #e5e7eb",borderRadius:6,background:"#fff",fontSize:11,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                  {p.l}
                </button>
              ))}
            </div>
          </div>
        )}

        {tipo==="chiusura" && (
          <div style={{background:"#f0fdf4",border:"1px solid #86efac",borderRadius:10,padding:16,marginBottom:16}}>
            <div style={{fontSize:12,fontWeight:700,color:"#065f46",marginBottom:6}}>📅 Periodo commessa (precompilato)</div>
            <div style={{fontSize:12,color:"#374151"}}>
              <strong>Apertura:</strong> {fmtData(dal)} &nbsp;→&nbsp; <strong>Chiusura:</strong> {fmtData(al)}
            </div>
            {projectData?.motivazioneChiusura && <div style={{fontSize:11,color:"#6b7280",marginTop:4}}>Motivo chiusura: <strong>{projectData.motivazioneChiusura}</strong></div>}
          </div>
        )}

        {/* Sezioni */}
        <div style={{background:"#f9fafb",border:"1px solid #e5e7eb",borderRadius:10,padding:16,marginBottom:16}}>
          <div style={{fontSize:12,fontWeight:700,color:"#374151",marginBottom:10}}>📑 Sezioni da includere</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
            {SEZIONI_DISPONIBILI.map(s=>{
              // Riservata = non disponibile in versione cliente; per la versione interna tutti gli admin la vedono
              const abilitata = versione==="cliente" ? !s.riservata : true;
              const selezionata = sezioni.includes(s.id);
              return (
                <button key={s.id} onClick={()=>toggleSez(s.id)} disabled={!abilitata}
                  style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",border:`1px solid ${selezionata&&abilitata?"#7c3aed":"#e5e7eb"}`,
                    borderRadius:7,background:selezionata&&abilitata?"#faf5ff":abilitata?"#fff":"#f9fafb",
                    cursor:abilitata?"pointer":"not-allowed",textAlign:"left",fontFamily:"'Inter',sans-serif",opacity:abilitata?1:.5}}>
                  <span style={{fontSize:14}}>{s.emoji}</span>
                  <span style={{fontSize:11,fontWeight:selezionata&&abilitata?700:400,color:selezionata&&abilitata?"#7c3aed":"#374151",flex:1}}>{s.label}</span>
                  {s.riservata && <Lock size={10} color="#d97706"/>}
                  {selezionata && abilitata && <CheckCircle size={12} color="#7c3aed"/>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Note */}
        <div style={{background:"#f9fafb",border:"1px solid #e5e7eb",borderRadius:10,padding:16}}>
          <div style={{fontSize:12,fontWeight:700,color:"#374151",marginBottom:10}}>📝 Note</div>
          <div style={{marginBottom:10}}>
            <label style={lbl}>Nota introduttiva</label>
            <textarea value={noteIntro} onChange={e=>setNoteIntro(e.target.value)} placeholder="Testo che apparirà nel riepilogo iniziale..."
              style={{...inp,minHeight:60,resize:"vertical"}}/>
          </div>
          <div>
            <label style={lbl}>Nota conclusiva</label>
            <textarea value={noteFinale} onChange={e=>setNoteFinale(e.target.value)} placeholder="Testo che apparirà in fondo al report..."
              style={{...inp,minHeight:60,resize:"vertical"}}/>
          </div>
        </div>
      </div>

      <div style={{background:"#fff",borderTop:"1px solid #e5e7eb",padding:"12px 20px",display:"flex",gap:10,justifyContent:"flex-end",flexShrink:0}}>
        <button onClick={onClose} style={{padding:"8px 16px",border:"1px solid #e5e7eb",borderRadius:8,background:"#fff",fontSize:12,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Annulla</button>
        <button onClick={()=>onGenera({tipo,versione,dal,al,sezioni,noteIntro,noteFinale})} disabled={loading}
          style={{padding:"8px 20px",border:"none",borderRadius:8,background:"#7c3aed",color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",gap:6}}>
          <Printer size={13}/> Genera PDF
        </button>
      </div>
    </div>
  );
}

// ─── Componente principale ────────────────────────────────────────────────────
export default function ReportCommessaTab({ projectId, projectData, orgId }) {
  const { currentUser, isOrgAdmin, isPlatformOwner } = useAuth();
  const { hasFeature, PLANS, PLAN_ORDER, orgPlan } = useFeatures();
  const { addToast } = useToast();
  const [reports, setReports] = useState([]);
  const [orgInfo, setOrgInfo] = useState(null);
  const [showWizard, setShowWizard] = useState(null); // "periodico" | "chiusura"
  const [generando, setGenerando] = useState(false);
  const [viewReport, setViewReport] = useState(null); // report da visualizzare a video

  const hasPiano = hasFeature("reportCommessa");

  // Report consolidato: i numeri salvati sono la fotografia del momento in cui è stato
  // generato. Accanto si mostra la situazione di oggi, così una differenza (attività
  // completate o aggiunte dopo) si capisce subito e non sembra un errore.
  const [attivitaOggi, setAttivitaOggi] = useState(null);
  useEffect(() => {
    if (!viewReport || !projectId || !orgId) { setAttivitaOggi(null); return; }
    getDocs(query(collection(db,"tasks"), where("projectId","==",projectId), where("organizationId","==",orgId)))
      .then(snap => { const t = snap.docs.map(d=>d.data()); setAttivitaOggi({ totale: t.length, completate: t.filter(x=>x.status==="completed"||x.status==="completata").length }); })
      .catch(() => setAttivitaOggi(null));
  }, [viewReport, projectId, orgId]);

  useEffect(()=>{
    if(!orgId) return;
    getDoc(doc(db,"organizations",orgId)).then(s=>{if(s.exists())setOrgInfo(s.data());});
  },[orgId]);

  useEffect(()=>{
    if(!projectId||!orgId||!hasPiano) return;
    getDocs(query(collection(db,"reportCommessa"),where("projectId","==",projectId),where("orgId","==",orgId)))
      .then(snap=>setReports(snap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(b.createdAt?.seconds||0)-(a.createdAt?.seconds||0))));
  },[projectId,orgId,hasPiano]);

  const generaReport = async (config) => {
    setGenerando(true);
    try {
      const dati = await raccogliDati(projectId, orgId, config.dal, config.al, config.sezioni, config.versione==="cliente");

      // Genera numerazione
      const tipo = config.tipo;
      const codice = TIPI_REPORT[tipo].codice;
      const anno = new Date().getFullYear();
      const prog = await prossimoProgressivo(`${orgId}_${codice.toLowerCase()}_${anno}`, { orgId, anno });
      const numero = `${codice}-${anno}-${String(prog).padStart(4,"0")}`;

      const configCompleta = { ...config, numero };

      // Salva in Firestore (snapshot leggero — solo metadati + config)
      await addDoc(collection(db,"reportCommessa"),{
        numero, tipo, versione:config.versione,
        dal:config.dal, al:config.al,
        sezioni:config.sezioni,
        noteIntro:config.noteIntro||"", noteFinale:config.noteFinale||"",
        projectId, projectName:projectData?.name||"",
        orgId, orgNome:orgInfo?.nome||orgInfo?.name||orgInfo?.name||"",
        stato:"consolidato",
        compilatoreUid:currentUser?.uid,
        compilatoreNome:currentUser?.displayName||"",
        createdAt:serverTimestamp(),
        // snapshot essenziale per ricostruibilità
        snapshot:{
          tasksCount: (dati.tasks||[]).length,
          completateCount: (dati.tasks||[]).filter(t=>t.status==="completed"||t.status==="completata").length,
          oreTotal: Object.values(dati.orePerDipendente||{}).reduce((s,h)=>s+h,0),
          sopralluoghiCount: (dati.sopralluoghi||[]).length,
          rapportiniCount: (dati.rapportini||[]).length,
          ddtCount: (dati.ddt||[]).length,
        }
      });

      // Refresh lista
      getDocs(query(collection(db,"reportCommessa"),where("projectId","==",projectId),where("orgId","==",orgId)))
        .then(snap=>setReports(snap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(b.createdAt?.seconds||0)-(a.createdAt?.seconds||0))));

      // Genera PDF
      generaPDF(configCompleta, dati, projectData, orgInfo, currentUser);
      setShowWizard(null);
      addToast(`${numero} generato ✅`,"success");
    } catch(err) {
      console.error(err);
      addToast("Errore nella generazione del report","error");
    }
    setGenerando(false);
  };

  // Rigenera PDF da snapshot salvato
  const rigenraPDF = async (r) => {
    try {
      const dati = await raccogliDati(projectId, orgId, r.dal, r.al, r.sezioni, r.versione==="cliente");
      generaPDF({ ...r }, dati, projectData, orgInfo, currentUser);
    } catch(err) {
      addToast("Errore nella rigenerazione del PDF","error");
    }
  };

  const eliminaReport = async id => {
    if(!await confirm("Eliminare questo report?",{type:"danger"})) return;
    await deleteDoc(doc(db,"reportCommessa",id));
    setReports(r=>r.filter(x=>x.id!==id));
    addToast("Report eliminato","info");
  };

  // ── Blocco piano non sufficiente ─────────────────────────────────────────────
  if (!hasPiano) {
    return (
      <div style={{padding:40,textAlign:"center"}}>
        <div style={{fontSize:48,marginBottom:16}}>📊</div>
        <div style={{fontSize:18,fontWeight:800,color:"#1e1b4b",marginBottom:8}}>Report Commessa</div>
        <div style={{fontSize:13,color:"#6b7280",marginBottom:20,maxWidth:400,margin:"0 auto 20px"}}>
          Il sistema di reportistica automatica è disponibile dal piano <strong style={{color:"#d97706"}}>Gold</strong>.
          Genera report periodici e fascicoli di chiusura in PDF professionale con tutti i dati già presenti in Nexiora.
        </div>
        <div style={{background:"#fffbeb",border:"1px solid #fde68a",borderRadius:12,padding:20,maxWidth:480,margin:"0 auto 20px",textAlign:"left"}}>
          <div style={{fontSize:12,fontWeight:700,color:"#92400e",marginBottom:12}}>✨ Piano Gold include:</div>
          {["Report Periodici automatici (settimanale, mensile, trimestrale)","Fascicolo riepilogativo di chiusura commessa","PDF professionale con logo aziendale","Versione Interna e versione Cliente","Sezione economica riservata","Archivio report consolidati","Numerazione progressiva RPC/RCC"].map(f=>(
            <div key={f} style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:"#374151",marginBottom:6}}>
              <CheckCircle size={13} color="#d97706"/> {f}
            </div>
          ))}
        </div>
        <div style={{fontSize:11,color:"#9ca3af"}}>
          Piano attuale: <strong>{PLANS[orgPlan]?.label||orgPlan}</strong> · Richiedi l'upgrade a <strong style={{color:"#d97706"}}>Gold</strong> contattando Nexiora
        </div>
      </div>
    );
  }

  if (showWizard) {
    return <WizardReport tipo={showWizard} projectData={projectData} orgId={orgId}
      onClose={()=>setShowWizard(null)} onGenera={generaReport}/>;
  }

  // Vista a video del report consolidato
  if (viewReport) {
    const r = viewReport;
    const tipo = TIPI_REPORT[r.tipo]||TIPI_REPORT.periodico;
    const stato = STATI_REPORT[r.stato]||STATI_REPORT.consolidato;
    return (
      <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
        <div style={{background:"#fff",borderBottom:"1px solid #e5e7eb",padding:"12px 20px",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
          <div>
            <div style={{fontSize:14,fontWeight:800,color:"#1e1b4b",display:"flex",alignItems:"center",gap:8}}>
              {tipo.emoji} <span style={{fontFamily:"monospace",color:"#7c3aed"}}>{r.numero}</span>
              <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:stato.bg,color:stato.col,fontWeight:700}}>{stato.label}</span>
              <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:r.versione==="cliente"?"#dbeafe":"#fef3c7",color:r.versione==="cliente"?"#1d4ed8":"#92400e",fontWeight:700}}>
                {r.versione==="cliente"?"Cliente":"Interno"}
              </span>
            </div>
            <div style={{fontSize:11,color:"#9ca3af",marginTop:2}}>
              {tipo.label} · {fmtData(r.dal)} — {fmtData(r.al)} · Generato da {r.compilatoreNome} il {fmtDataOra(r.createdAt)}
            </div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>rigenraPDF(r)}
              style={{display:"flex",alignItems:"center",gap:5,padding:"7px 14px",border:"1px solid #7c3aed",borderRadius:8,background:"#faf5ff",color:"#7c3aed",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
              <Printer size={13}/> Stampa PDF
            </button>
            <button onClick={()=>setViewReport(null)}
              style={{padding:"7px 10px",border:"1px solid #e5e7eb",borderRadius:8,background:"#fff",cursor:"pointer"}}>
              <X size={14}/>
            </button>
          </div>
        </div>
        <div style={{flex:1,overflowY:"auto",padding:24}}>
          {/* Copertina */}
          <div style={{background:"#f9fafb",border:"1px solid #e5e7eb",borderRadius:12,padding:24,marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
            <div>
              <div style={{fontSize:10,fontWeight:700,color:"#7c3aed",textTransform:"uppercase",letterSpacing:".08em",marginBottom:4}}>{tipo.emoji} {tipo.label}</div>
              <div style={{fontSize:22,fontWeight:900,color:"#1e1b4b",marginBottom:4}}>{projectData?.name}</div>
              <div style={{fontSize:13,color:"#6b7280"}}>{projectData?.clienteNome}</div>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontFamily:"monospace",fontSize:18,fontWeight:900,color:"#7c3aed"}}>{r.numero}</div>
              <div style={{fontSize:11,color:"#9ca3af",marginTop:4}}>{fmtData(r.dal)} — {fmtData(r.al)}</div>
            </div>
          </div>
          {/* Dati commessa */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:16}}>
            {[
              {l:"Codice commessa",v:projectData?.codiceCommessa||"—"},
              {l:"Responsabile",v:projectData?.responsabileNome||"—"},
              {l:"Stato commessa",v:projectData?.statoCommessa==="chiusa"?"🔒 Chiusa":"🟢 Attiva"},
              {l:"Sezioni incluse",v:r.sezioni?.length||0},
              {l:"Generato da",v:r.compilatoreNome||"—"},
              {l:"Data generazione",v:fmtDataOra(r.createdAt)},
            ].map(f=>(
              <div key={f.l} style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:8,padding:"10px 14px"}}>
                <div style={{fontSize:9,color:"#9ca3af",fontWeight:700,textTransform:"uppercase",marginBottom:4}}>{f.l}</div>
                <div style={{fontSize:13,fontWeight:700,color:"#1e1b4b"}}>{f.v}</div>
              </div>
            ))}
          </div>
          {/* Snapshot dati */}
          {r.snapshot && (
            <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,padding:16,marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>📊 Fotografia al {fmtDataOra(r.createdAt)}</div>
              <div style={{fontSize:11,color:"#6b7280",marginBottom:12}}>Sono i numeri del momento in cui il report è stato generato e non cambiano più.
                {attivitaOggi && (attivitaOggi.totale!==r.snapshot.tasksCount || attivitaOggi.completate!==r.snapshot.completateCount) &&
                  <span style={{display:"inline-block",marginLeft:6,padding:"1px 8px",borderRadius:8,background:"#ede9fe",color:"#5b21b6",fontWeight:700}}>
                    Oggi: {attivitaOggi.totale} attività, {attivitaOggi.completate} completate
                  </span>}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:10}}>
                {[
                  {l:"Attività totali",v:r.snapshot.tasksCount,c:"#1e1b4b"},
                  {l:"Completate",v:r.snapshot.completateCount,c:"#059669"},
                  {l:"Ore imputate",v:`${r.snapshot.oreTotal?.toFixed(1)||0}h`,c:"#2563eb"},
                  {l:"Sopralluoghi",v:r.snapshot.sopralluoghiCount,c:"#7c3aed"},
                  {l:"DDT",v:r.snapshot.ddtCount,c:"#d97706"},
                ].map(k=>(
                  <div key={k.l} style={{background:"#f9fafb",border:"1px solid #e5e7eb",borderRadius:8,padding:"10px",textAlign:"center"}}>
                    <div style={{fontSize:22,fontWeight:900,color:k.c}}>{k.v}</div>
                    <div style={{fontSize:9,color:"#6b7280",fontWeight:700,textTransform:"uppercase",marginTop:3}}>{k.l}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Sezioni incluse */}
          <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,padding:16,marginBottom:16}}>
            <div style={{fontSize:12,fontWeight:700,color:"#374151",marginBottom:10}}>📑 Sezioni incluse nel report</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
              {(r.sezioni||[]).map(s=>{
                const def=SEZIONI_DISPONIBILI.find(x=>x.id===s);
                return def?(
                  <span key={s} style={{padding:"4px 10px",background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:20,fontSize:11,color:"#7c3aed",fontWeight:600}}>
                    {def.emoji} {def.label}
                  </span>
                ):null;
              })}
            </div>
          </div>
          {/* Note */}
          {r.noteIntro && <div style={{background:"#fef3c7",border:"1px solid #fde68a",borderRadius:8,padding:"10px 14px",marginBottom:10,fontSize:12}}><strong>Nota introduttiva:</strong> {r.noteIntro}</div>}
          {r.noteFinale && <div style={{background:"#fef3c7",border:"1px solid #fde68a",borderRadius:8,padding:"10px 14px",fontSize:12}}><strong>Nota conclusiva:</strong> {r.noteFinale}</div>}
          <div style={{marginTop:20,textAlign:"center"}}>
            <button onClick={()=>rigenraPDF(r)}
              style={{display:"inline-flex",alignItems:"center",gap:6,padding:"10px 24px",border:"none",borderRadius:8,background:"#7c3aed",color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
              <Printer size={14}/> Rigenera e stampa PDF completo
            </button>
            <div style={{fontSize:11,color:"#9ca3af",marginTop:8}}>Il PDF viene rigenerato con i dati attuali della commessa per il periodo del report: può differire dalla fotografia qui sopra</div>
          </div>
        </div>
      </div>
    );
  }

  const isChiusa = projectData?.statoCommessa === "chiusa";

  return (
    <div style={{padding:20}}>
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:20,flexWrap:"wrap"}}>
        <h3 style={{fontSize:15,fontWeight:700,color:"#1e1b4b",margin:0,display:"flex",alignItems:"center",gap:6}}>
          <FileText size={15}/> Report Commessa
        </h3>
        <button onClick={()=>setShowWizard("periodico")}
          style={{display:"flex",alignItems:"center",gap:5,padding:"6px 12px",border:"1px solid #7c3aed",borderRadius:8,background:"#faf5ff",color:"#7c3aed",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
          <Plus size={13}/> Nuovo Report Periodico
        </button>
        {isChiusa && (
          <button onClick={()=>setShowWizard("chiusura")}
            style={{display:"flex",alignItems:"center",gap:5,padding:"6px 12px",border:"1px solid #059669",borderRadius:8,background:"#f0fdf4",color:"#059669",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
            <FileText size={13}/> Genera Report di Chiusura
          </button>
        )}
        {!isChiusa && (
          <div style={{fontSize:11,color:"#9ca3af",display:"flex",alignItems:"center",gap:4}}>
            <Lock size={11}/> Il Report di Chiusura è disponibile dopo la chiusura della commessa
          </div>
        )}
        <span style={{fontSize:11,color:"#9ca3af",marginLeft:"auto"}}>{reports.length} report/i</span>
      </div>

      {/* Info fonti dati */}
      {reports.length===0 && (
        <div style={{background:"#f0f9ff",border:"1px solid #bae6fd",borderRadius:10,padding:16,marginBottom:20}}>
          <div style={{fontSize:12,fontWeight:700,color:"#0369a1",marginBottom:8}}>💡 Dati disponibili per questa commessa</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,fontSize:11,color:"#374151"}}>
            {[
              {e:"✅",l:"Attività e sottoattività"},
              {e:"📅",l:"Cronoprogramma"},
              {e:"👷",l:"Presenze e ore"},
              {e:"📒",l:"Giornale di cantiere"},
              {e:"🔍",l:"Sopralluoghi"},
              {e:"📋",l:"Rapportini intervento"},
              {e:"📄",l:"DDT"},
              {e:"📝",l:"Verbali riunione"},
              {e:"💼",l:"Inoltro costi per offerta al cliente"},
              {e:"💰",l:"Dati economici"},
              {e:"📆",l:"Calendario commessa"},
              {e:"📌",l:"Timeline eventi"},
            ].map(f=>(
              <div key={f.l} style={{display:"flex",alignItems:"center",gap:6,padding:"5px 8px",background:"#fff",borderRadius:6,border:"1px solid #e0f2fe"}}>
                <span>{f.e}</span><span style={{fontSize:10}}>{f.l}</span>
              </div>
            ))}
          </div>
          <div style={{fontSize:11,color:"#0369a1",marginTop:8}}>Nexiora raccoglierà automaticamente tutti i dati disponibili al momento della generazione.</div>
        </div>
      )}

      {/* Archivio report */}
      {reports.length===0 ? (
        <div style={{textAlign:"center",padding:60,color:"#9ca3af"}}>
          <FileText size={48} style={{opacity:.15,marginBottom:12}}/>
          <div style={{fontSize:14,fontWeight:600}}>Nessun report generato</div>
          <div style={{fontSize:12,marginTop:4}}>Crea il primo report periodico o, a commessa chiusa, il fascicolo di chiusura</div>
        </div>
      ):(
        <div>
          {reports.map((r,i)=>{
            const stato = STATI_REPORT[r.stato]||STATI_REPORT.consolidato;
            const tipo = TIPI_REPORT[r.tipo]||TIPI_REPORT.periodico;
            return (
              <div key={r.id} style={{border:"1px solid #e5e7eb",borderRadius:10,padding:"12px 16px",marginBottom:10,background:"#fff",display:"flex",alignItems:"center",gap:12}}>
                <div style={{fontSize:24,flexShrink:0}}>{tipo.emoji}</div>
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:2}}>
                    <span style={{fontFamily:"monospace",fontWeight:800,color:"#7c3aed",fontSize:13}}>{r.numero}</span>
                    <span style={{fontSize:11,padding:"1px 7px",borderRadius:10,background:stato.bg,color:stato.col,fontWeight:700}}>{stato.label}</span>
                    <span style={{fontSize:11,padding:"1px 7px",borderRadius:10,background:r.versione==="cliente"?"#dbeafe":"#fef3c7",color:r.versione==="cliente"?"#1d4ed8":"#92400e",fontWeight:700}}>
                      {r.versione==="cliente"?"Cliente":"Interno"}
                    </span>
                  </div>
                  <div style={{fontSize:11,color:"#6b7280"}}>
                    {tipo.label} · {r.versione==="chiusura"?"Intero ciclo":(`${fmtData(r.dal)} — ${fmtData(r.al)}`)}
                    {r.compilatoreNome && ` · ${r.compilatoreNome}`}
                  </div>
                  {r.snapshot && (
                    <div style={{fontSize:10,color:"#9ca3af",marginTop:3}}>
                      {r.snapshot.tasksCount} attività · {r.snapshot.completateCount} completate · {r.snapshot.oreTotal?.toFixed(1)}h · {r.snapshot.sopralluoghiCount} sopralluoghi · {r.snapshot.ddtCount} DDT
                    </div>
                  )}
                </div>
                <div style={{fontSize:11,color:"#9ca3af",whiteSpace:"nowrap"}}>{fmtDataOra(r.createdAt)}</div>
                <button onClick={()=>rigenraPDF(r)} title="Stampa / Visualizza PDF"
                  style={{display:"flex",alignItems:"center",gap:4,padding:"5px 10px",border:"1px solid #7c3aed",borderRadius:7,background:"#faf5ff",color:"#7c3aed",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif",flexShrink:0}}>
                  <Printer size={12}/> Stampa
                </button>
                <button onClick={()=>setViewReport(r)} title="Visualizza a video"
                  style={{display:"flex",alignItems:"center",gap:4,padding:"5px 10px",border:"1px solid #e5e7eb",borderRadius:7,background:"#fff",color:"#374151",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif",flexShrink:0}}>
                  <FileText size={12}/> Vedi
                </button>
                <button onClick={()=>eliminaReport(r.id)} style={{background:"none",border:"none",cursor:"pointer",color:"#fca5a5",padding:4}} title="Elimina">
                  <X size={14}/>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
